/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Sep 20 2023
 **/

import { useMemo } from "react";
import { useAtomValue } from "jotai";

import { Button, Column, Row, Typography } from "@/frameworks/designStuff/components";
import { usePopover } from "@/frameworks/core/hooks";
import { GROUP_POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { Maybe } from "@/frameworks/libraries/components";

import * as S from "./emotion";
import FileImage from "./FileImage";
import { FileMoreMenu } from "../FileMoreMenu";
import { useFileSelect } from "../hooks";
import { FileDescriptor, FileName, FileUploader } from "./components";
import { listFileShare3DAtom } from "./atom";

interface FileCard {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
	status?: "waiting" | "in-use" | "used";
}

export default function FileCard({ file, status = "waiting" }: FileCard) {
	const popover = usePopover();
	const { handleSelectFile } = useFileSelect(file);
	const listFileShare3D = useAtomValue(listFileShare3DAtom);

	const isShare3D = useMemo(() => {
		return listFileShare3D.includes(file.id);
	}, [listFileShare3D]);

	const handleClickMore = (ev: React.MouseEvent<HTMLButtonElement>) => {
		ev.stopPropagation();

		popover.show({
			popoverID: GROUP_POPOVER_ID.FILE_MORE_MENU,
			uniqueID: file.id,
			content: (
				<FileMoreMenu
					file={file}
					onClickMenu={() => {
						popover.hide(GROUP_POPOVER_ID.FILE_MORE_MENU);
					}}
					isShare3D={isShare3D}
				/>
			),
			zIndex: ZIndex.FILEBOX + 1,
			anchorEl: ev.currentTarget,
			direction: { x: "left", y: "bottom" }
		});
	};

	return (
		<S.CardWrapper tabIndex={0} status={status} isShare3D={isShare3D} onDoubleClick={handleSelectFile}>
			<FileImage file={file} isShare3D={isShare3D} />
			<S.FileInfoWrapper gap={12}>
				<Column css={{ width: "calc(100% - 32px - 12px)" }}>
					<FileName file={file} isShare3D={isShare3D} />
					<Maybe test={file.fileBoxType === "LINK"}>
						<Typography
							typoSize="Body5"
							typoColor="neutral_70_100"
							align="left"
							maxLine={1}
							ellipsis
							css={{ width: "100%" }}
						>
							{file.link}
						</Typography>
					</Maybe>
					<Row verticalAlign="center" style={{ flexWrap: "wrap", width: "100%" }} gap={"4px"}>
						<FileUploader file={file} isShare3D={isShare3D} />
						<FileDescriptor file={file} isShare3D={isShare3D} />
					</Row>
				</Column>
				<Button.Icon
					name="More24"
					size="large"
					filled="transparent"
					fill={status === "in-use" ? "neutral_00_100" : "neutral_80_100"}
					onClick={handleClickMore}
				/>
			</S.FileInfoWrapper>
		</S.CardWrapper>
	);
}
